import React from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { Box, Button, Typography } from "@mui/material";
import { SelectDateRange } from "components/forms";
import { GroupByCurrency } from "components/reports/Group-by-currency/GroupByCurrency";
import { GroupByMenu } from "components/reports/Group-by-menu/GroupByMenu";
import { useReportsQueriesSelector } from "store";
import type { CardFilters, DateRangeValue, ReportsQueries } from "store/types";

import { SectionLabel } from "./SectionLabel";

interface Props {
  cardFilters?: CardFilters;
  onUpdate: (f: CardFilters | undefined) => void;
  // Cards backed by ratio measures (e.g. Performance Attribution) are currency-independent,
  // so the currency control is hidden for them.
  hideCurrency?: boolean;
}

// Investor (and asset-class) filtering is hidden for now: there's no way to surface an applied
// investor filter on the card itself, which makes a generated PDF ambiguous about who it's for.
// Only the per-card date range (shown via the card chip) and currency remain.
const isEmptyFilters = (f: CardFilters): boolean =>
  f.investors.length === 0 && !f.currency && !f.exchangeMethod && !f.dateRange;

// Match the outlined look of AutocompleteMultiSelect (small size, theme defaults).
const CONTROL_HEIGHT = "38px";
const CONTROL_RADIUS = "8px";
const BORDER_COLOR = "rgba(0, 0, 0, 0.23)";
const BORDER_COLOR_HOVER = "rgba(0, 0, 0, 0.87)";
const ICON_COLOR = "rgba(0, 0, 0, 0.54)";

export const CardFilterSettings: React.FC<Props> = ({ cardFilters, onUpdate, hideCurrency }) => {
  const globalQueries = useReportsQueriesSelector();
  const filters: CardFilters = cardFilters ?? { investors: [] };
  const hasFilters = !isEmptyFilters(filters);

  const updateFilters = (next: CardFilters) => {
    onUpdate(isEmptyFilters(next) ? undefined : next);
  };

  const handleDateRangeChange = (value: DateRangeValue) => {
    updateFilters({ ...filters, dateRange: value });
  };

  // Reuse the global currency selector UI (GroupByMenu + GroupByCurrency), but commit to the local card filters.
  const effectiveCurrency = filters.currency ?? globalQueries.currency;
  const effectiveHedging = filters.exchangeMethod ?? globalQueries.exchangeMethod;
  const currencyDefaults: Partial<ReportsQueries> = {
    currency: effectiveCurrency,
    exchangeMethod: effectiveHedging
  };

  const handleCurrencyOkay = (values: Partial<ReportsQueries>) => {
    updateFilters({
      ...filters,
      currency: values.currency ?? effectiveCurrency,
      exchangeMethod: values.exchangeMethod ?? effectiveHedging
    });
  };

  const handleCurrencyReset = () => {
    updateFilters({ ...filters, currency: undefined, exchangeMethod: undefined });
  };

  return (
    <Box>
      <SectionLabel>Card Filters</SectionLabel>

      <Box sx={{ mt: "4px" }}>
        <SelectDateRange
          formControlProps={{ sx: { minWidth: "100%", width: "100%" } }}
          selectProps={{
            startAdornment: <TodayOutlinedIcon sx={{ fontSize: "15px", color: ICON_COLOR }} />,
            sx: {
              height: CONTROL_HEIGHT,
              borderRadius: CONTROL_RADIUS,
              backgroundColor: "#FFFFFF",
              fontSize: 16,
              color: "#0a3142",
              "&.MuiInputBase-root": { height: CONTROL_HEIGHT, paddingRight: "32px" },
              ".MuiSelect-icon": { color: "#0a3142", right: "12px" },
              ".MuiOutlinedInput-notchedOutline": { borderColor: BORDER_COLOR }
            }
          }}
          label=""
          value={filters.dateRange ?? globalQueries.dateRange}
          onChange={handleDateRangeChange}
        />
      </Box>

      {!hideCurrency && (
        <Box sx={{ mt: "8px" }}>
          <GroupByMenu
            onOkay={handleCurrencyOkay}
            onReset={handleCurrencyReset}
            renderTrigger={({ onClick }) => (
              <Button
                onClick={onClick}
                endIcon={<ArrowDropDownIcon />}
                sx={{
                  height: CONTROL_HEIGHT,
                  width: "100%",
                  minWidth: 0,
                  justifyContent: "space-between",
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 400,
                  color: "#0a3142",
                  bgcolor: "#FFFFFF",
                  border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: CONTROL_RADIUS,
                  pl: "14px",
                  pr: "9px",
                  "&:hover": { bgcolor: "#FFFFFF", borderColor: BORDER_COLOR_HOVER },
                  "& .MuiButton-endIcon": { color: "#0a3142", marginLeft: "4px", marginRight: "2px" }
                }}
              >
                {effectiveCurrency}
              </Button>
            )}
          >
            <GroupByCurrency defaultQueries={currencyDefaults} />
          </GroupByMenu>
        </Box>
      )}

      {!hasFilters && <Typography sx={{ fontSize: 11, color: "#D1D5DB", mt: "8px" }}>Using global filters</Typography>}
    </Box>
  );
};
